import {GetWidgetDataResponse} from '@wix/ambassador-events-ui-v1-widget-data/types'
import {addQueryParams, stringifyParams} from '@wix/panda-js-utils'
import {
  EVENT_FILTER_TYPE,
  EVENT_SORT_ORDER,
  EventsHttpClient,
  LIST_LAYOUT,
  RECURRING_FILTER,
  WIDGET_TYPE,
  WidgetData,
} from '@wix/wix-events-commons-statics'
import {getServerlessWidgetDataAdapter} from './serverless-widget-data'

export interface CommonWidgetDataParams {
  language: string
  locale: string
  tz: string
  viewMode: string
  members: boolean
  paidPlans: boolean
  responsive: boolean
  newClassicEvents?: boolean
  editor3?: boolean
}

export interface GetWidgetDataParams extends CommonWidgetDataParams {
  compId: string
  showcase: boolean
  fetchCategoryId?: string[]
  fetchAllCategories?: boolean
  widgetType?: WIDGET_TYPE
  listLayout?: LIST_LAYOUT
  recurringFilter?: RECURRING_FILTER
  filterType?: EVENT_FILTER_TYPE
  sortOrder?: EVENT_SORT_ORDER
  categoryId?: string | string[]
  noCategoryIdFallback?: boolean
  limit?: number
  useServerless: boolean
  calendarEvents?: boolean
}

export const getWidgetData =
  (api: EventsHttpClient) =>
  async (args: GetWidgetDataParams): Promise<WidgetData | GetWidgetDataResponse> => {
    const {
      responsive = false,
      locale,
      language,
      compId,
      viewMode,
      members,
      paidPlans,
      widgetType,
      listLayout,
      recurringFilter,
      showcase = false,
      tz,
      filterType,
      sortOrder,
      categoryId,
      noCategoryIdFallback,
      limit,
      newClassicEvents,
      fetchCategoryId,
      fetchAllCategories,
      editor3,
      useServerless = false,
      calendarEvents = false,
    } = args

    if (!useServerless) {
      return api.get<WidgetData>(
        addQueryParams(`/html/v2/widget-data`, {
          ...stringifyParams({
            compId,
            locale: language,
            regional: locale,
            viewMode,
            members,
            paidPlans,
            responsive,
            widgetType,
            listLayout,
            calendarEvents,
            recurringFilter,
            showcase,
            tz,
            filterType,
            sortOrder,
            noCategoryIdFallback,
            limit,
            newClassicEvents,
            editor3,
            fetchBadges: true,
            ...((calendarEvents || listLayout === LIST_LAYOUT.CALENDAR) && {
              today: new Date().toLocaleDateString('en-CA', {timeZone: tz}), // So the cache resets at least everyday
            }),
          }),
          categoryId,
          fetchCategoryId,
          fetchAllCategories,
        }),
      )
    }

    const data = getServerlessWidgetDataAdapter(api)(args)

    return data
  }

export interface GetFeaturedWidgetDataParams extends CommonWidgetDataParams {
  eventId: string
  categoryId: string
  manuallySelected: boolean
}

export const getFeaturedWidgetData =
  (api: EventsHttpClient) =>
  async ({responsive = false, locale, language, ...rest}: GetFeaturedWidgetDataParams): Promise<WidgetData> =>
    api.get(
      addQueryParams(
        '/html/featured-widget-data',
        stringifyParams({
          responsive,
          locale: language,
          regional: locale,
          fetchBadges: true,
          ...rest,
        }),
      ),
    )
